import React from 'react';
import { colors } from './constants';
import { calculateDuration } from './helpers';
import { ClipboardIcon, HospitalIcon, BookmarkIcon } from './Icons';

const SidePanel = ({ 
  selectedBeds, 
  displayBed, 
  currentCategory, 
  totalDailyCharge,
  handleAllocateBeds,
  handleReserveBeds,
  handleClearSelection
}) => {
  const styles = {
    sidePanel: {
      background: colors.cream100,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '12px',
      padding: '24px',
      height: 'fit-content'
    },
    h3: {
      fontSize: '16px',
      fontWeight: 550,
      marginTop: '0',
      marginBottom: '16px',
      color: colors.slate900
    },
    infoSection: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
      fontSize: '13px'
    },
    infoLabel: {
      color: colors.slate500
    },
    infoValue: {
      fontWeight: 500,
      color: colors.slate900
    },
    selectedBedsBox: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    selectedBedsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '12px'
    },
    selectedBedChip: {
      background: colors.orange500,
      color: colors.cream50,
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 500
    },
    pricingBox: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    totalCharge: {
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: `2px solid rgba(94, 82, 64, 0.12)`,
      fontSize: '16px',
      fontWeight: 600,
      color: colors.teal600
    },
    btnPrimary: {
      padding: '12px 16px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center',
      background: colors.teal500,
      color: colors.cream50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      marginBottom: '12px'
    },
    btnSecondary: {
      padding: '12px 16px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center',
      background: 'rgba(94, 82, 64, 0.12)',
      color: colors.slate900,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      marginBottom: '12px'
    },
    btnOutline: {
      padding: '12px 16px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center',
      background: 'transparent',
      color: colors.slate900,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%'
    },
    btnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    equipmentList: {
      marginTop: '8px',
      fontSize: '12px',
      color: colors.slate500
    }
  };

  return (
    <aside style={styles.sidePanel}>
      {displayBed && (
        <div style={styles.infoSection}>
          <h3 style={styles.h3}>Bed Information</h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Bed ID:</span>
            <span style={styles.infoValue}>{displayBed.id}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Status:</span>
            <span style={styles.infoValue}>{displayBed.status}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Type:</span>
            <span style={styles.infoValue}>{displayBed.wardType}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Floor:</span>
            <span style={styles.infoValue}>Floor {displayBed.floor}</span>
          </div>
          {displayBed.patient && (
            <>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Patient:</span>
                <span style={styles.infoValue}>{displayBed.patient}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Doctor:</span>
                <span style={styles.infoValue}>{displayBed.doctor}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Days Admitted:</span>
                <span style={styles.infoValue}>{calculateDuration(displayBed.admissionDate)}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Contact:</span>
                <span style={styles.infoValue}>{displayBed.contact}</span>
              </div>
            </>
          )}
          {displayBed.equipment && displayBed.equipment.length > 0 && (
            <div style={styles.equipmentList}>
              <strong>Equipment:</strong> {displayBed.equipment.join(', ')}
            </div>
          )}
          {displayBed.notes && (
            <div style={{ ...styles.equipmentList, marginTop: '12px' }}>
              <strong>Notes:</strong> {displayBed.notes}
            </div>
          )}
        </div>
      )}

      {selectedBeds.length > 0 && (
        <div style={styles.selectedBedsBox}>
          <h3 style={styles.h3}>Selected Beds ({selectedBeds.length})</h3>
          <div style={styles.selectedBedsList}>
            {selectedBeds.map(bed => (
              <span key={bed.id} style={styles.selectedBedChip}>
                {bed.id}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={styles.pricingBox}>
        <h3 style={styles.h3}>Daily Charges</h3>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Bed Charge:</span>
          <span style={styles.infoValue}>₹{currentCategory.price.toLocaleString()}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Nursing:</span>
          <span style={styles.infoValue}>₹{currentCategory.nursing.toLocaleString()}</span>
        </div>
        <div style={styles.infoRow}>
          <span style={styles.infoLabel}>Equipment:</span>
          <span style={styles.infoValue}>₹{currentCategory.equipment.toLocaleString()}</span>
        </div>
        <div style={styles.totalCharge}>
          Total: ₹{totalDailyCharge.toLocaleString()}/day
        </div>
      </div>

      <button 
        style={selectedBeds.length === 0 ? { ...styles.btnPrimary, ...styles.btnDisabled } : styles.btnPrimary}
        onClick={handleAllocateBeds}
        disabled={selectedBeds.length === 0}
        onMouseEnter={(e) => {
          if (selectedBeds.length > 0) {
            e.currentTarget.style.background = colors.teal600;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 128, 141, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (selectedBeds.length > 0) {
            e.currentTarget.style.background = colors.teal500;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <ClipboardIcon />
        Admit Patient
      </button>
      
      <button 
        style={selectedBeds.length === 0 ? { ...styles.btnSecondary, ...styles.btnDisabled } : styles.btnSecondary}
        onClick={handleReserveBeds}
        disabled={selectedBeds.length === 0}
        onMouseEnter={(e) => {
          if (selectedBeds.length > 0) {
            e.currentTarget.style.background = 'rgba(94, 82, 64, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (selectedBeds.length > 0) {
            e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
          }
        }}
      >
        <BookmarkIcon />
        Reserve Beds
      </button>
      
      {selectedBeds.length > 0 && (
        <button 
          style={styles.btnOutline}
          onClick={handleClearSelection}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          Clear Selection
        </button>
      )}
    </aside>
  );
};

export default SidePanel;
